<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue';
import { useThemeVars } from 'naive-ui';
import type { Config, Data, Layout } from 'plotly.js';
import type { SunburstData } from '@/utils/llmUsageStatistics';

interface Props {
  title: string;
  data: SunburstData;
  height?: string;
  emptyDescription?: string;
  formatValue?: (value: number) => string;
}

const props = withDefaults(defineProps<Props>(), {
  height: '480px',
  emptyDescription: 'No data',
  formatValue: (value: number) => value.toLocaleString(),
});

const PlotlyPlot = defineAsyncComponent(() => import('@/components/charts/PlotlyPlot.vue'));
const themeVars = useThemeVars();

const hasData = computed(() => props.data.ids.length > 0);

const chartData = computed<Data[]>(() => {
  const formatted = props.data.values.map(value => props.formatValue(value));
  return [
    {
      type: 'sunburst',
      ids: props.data.ids,
      labels: props.data.labels,
      parents: props.data.parents,
      values: props.data.values,
      customdata: formatted,
      branchvalues: 'total',
      marker: { colors: props.data.colors },
      // Click a slice to zoom in, click the centre to zoom back out.
      hovertemplate: '<b>%{label}</b><br>%{customdata} (%{percentRoot:.1%})<extra></extra>',
      insidetextorientation: 'radial',
      sort: true,
    } as unknown as Data,
  ];
});

const chartLayout = computed<Partial<Layout>>(() => ({
  paper_bgcolor: 'transparent',
  plot_bgcolor: 'transparent',
  font: {
    color: themeVars.value.textColor2,
    family: themeVars.value.fontFamily,
  },
  margin: { t: 8, r: 8, b: 8, l: 8 },
}));

const chartConfig = computed<Partial<Config>>(() => ({
  displayModeBar: false,
  scrollZoom: false,
}));
</script>

<template>
  <n-card :title="title">
    <PlotlyPlot
      v-if="hasData"
      :data="chartData"
      :layout="chartLayout"
      :config="chartConfig"
      :height="height"
    />
    <n-empty v-else :description="emptyDescription" />
  </n-card>
</template>
